import {
	IonButton,
	IonButtons,
	IonFooter,
	IonHeader,
	IonIcon,
	IonPage,
	IonText,
	IonTitle,
	useIonRouter,
} from "@ionic/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import { useStore } from "zustand";
import { ChatContent } from "@/components/Chat/Content";
import { ChatFooter } from "@/components/Chat/Footer";
import { ChatInput } from "@/components/Chat/Input";
import { ChatMessage } from "@/components/Chat/Message";
import { ChatStoreContext, useChatStore } from "@/components/Chat/store";
import { Button } from "@/components/Global/Button";
import { Content } from "@/components/Global/Content";
import { Markdown } from "@/components/Global/Markdown";
import { Toolbar } from "@/components/Global/Toolbar";
import { backendService } from "@/lib/backend";
import { type RefinePersonalMissionDto } from "@/lib/backend/api";
import { arrowLeftIcon } from "@/lib/icons/@heroicons/react/20/solid";
import { queryOptions_myPersonalMission } from "@/lib/query/functions/personal-mission/my";
import { useMutationStream } from "@/lib/query/hooks/useMutationStream";
import { Routes } from "@/lib/router";

export function YourMissionChat() {
	const { t } = useTranslation();
	const router = useIonRouter();

	const chatStore = useChatStore();
	const chat = useStore(chatStore);
	const queryPersonalMission = useQuery(queryOptions_myPersonalMission());
	const hasAutoStarted = useRef(false);
	const personalMission = queryPersonalMission.data;
	const [declinedProposals, setDeclinedProposals] = useState<Set<string>>(new Set());

	const [streamedResponse, mutationStream] = useMutationStream({
		mutationKey: ["personal-mission", "refine"],
		mutationFn: (params, variables: RefinePersonalMissionDto) =>
			backendService.personalMission.personalMissionControllerRefine(variables, params),
		onSettled: async () => {
			await queryPersonalMission.refetch();
			chat.setResponding(false);
		},
	});

	const acceptProposalMutation = useMutation({
		mutationFn: (proposalId: string) =>
			backendService.personalMission.personalMissionControllerAcceptRefineProposal(proposalId),

		onSuccess: async () => {
			await queryPersonalMission.refetch();
		},
	});

	const declineProposalMutation = useMutation({
		mutationFn: (proposalId: string) =>
			backendService.personalMission.personalMissionControllerDeclineRefineProposal(proposalId),
		onSuccess: async () => {
			await queryPersonalMission.refetch();
		},
	});

	useEffect(() => {
		const hasNoMessages = personalMission?.messages.length === 0;
		const isIdle = !mutationStream.isPending && !queryPersonalMission.isFetching;
		const shouldAutoStart = hasNoMessages && isIdle && !hasAutoStarted.current;

		if (shouldAutoStart) {
			hasAutoStarted.current = true;
			mutationStream.mutateAsync({ message: "" });
			chat.setResponding(true);
		}
	}, [
		personalMission?.messages.length,
		mutationStream.isPending,
		queryPersonalMission.isFetching,
		mutationStream,
		chat,
	]);

	useEffect(() => {
		const shouldUpdateStream =
			!mutationStream.isPending && streamedResponse !== chat.streamAssistantMessage;

		if (shouldUpdateStream) {
			chat.setStreamAssistantMessage(streamedResponse);
		}
	}, [streamedResponse, mutationStream.isPending, chat]);

	const filteredMessages = !personalMission?.messages
		? []
		: personalMission.messages
				.filter(
					(msg) =>
						msg.messageRole === "USER" ||
						(msg.messageRole === "ASSISTANT" && msg.content) ||
						msg.messageRole === "PROPOSAL",
				)
				.toSorted((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

	const unansweredProposal = personalMission?.messages?.find(
		(msg) =>
			msg.messageRole === "PROPOSAL" &&
			msg.missionProposal?.answered === false &&
			!declinedProposals.has(msg.id),
	);

	return (
		<IonPage>
			<IonHeader className="ion-no-border">
				<Toolbar className="ion-p-2 plt-desktop:sm:ion-px-[max(calc(calc(100vw-640px)/2),--spacing(4))]">
					<IonButtons slot="secondary">
						<IonButton
							data-attr="page.your-mission.chat.back"
							data-touch-target
							routerLink={Routes.YourMission()}
							routerDirection="back"
							color="light"
							className="part-icon:m-0 part-icon:size-5 part-native:size-10 part-native:min-h-0 part-native:rounded-full part-native:bg-brown-300 part-native:p-1 part-native:text-xs part-native:text-brown-600 min-h-0"
						>
							<IonIcon icon={arrowLeftIcon} />
						</IonButton>
					</IonButtons>
					<IonTitle>{t("your-mission.chat.title")}</IonTitle>
				</Toolbar>
			</IonHeader>
			<ChatStoreContext.Provider value={chatStore}>
				<Content className="part-scroll:gap-8">
					<ChatContent>
						{!!personalMission && (
							<motion.div
								className="border-brown-200 bg-brown-100 flex flex-col gap-2 rounded-xl border p-4"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								transition={{ delay: 0.2 }}
							>
								<IonText className="text-brown-700 text-lg font-bold text-pretty">
									{t("your-mission.dashboard.title")}
								</IonText>
								<IonText className="text-brown-700 text-pretty">
									<Markdown>{personalMission.personalMission}</Markdown>
								</IonText>
							</motion.div>
						)}
						{filteredMessages.map((message) => (
							<div key={message.id} className="flex flex-col gap-4">
								{message.messageRole === "ASSISTANT" && message.content && (
									<ChatMessage sender="bot">
										<Markdown>{message.content}</Markdown>
									</ChatMessage>
								)}
								{message.messageRole === "USER" && (
									<ChatMessage sender="user">{message.content}</ChatMessage>
								)}
								{message.messageRole === "PROPOSAL" && message.missionProposal && (
									<>
										<ChatMessage sender="bot">
											<Markdown>{message.missionProposal.mission}</Markdown>
										</ChatMessage>
										{!message.missionProposal.answered && !declinedProposals.has(message.id) && (
											<ChatMessage className="w-full px-2" sender="bot">
												<IonText className="text-brown-700 px-1 font-medium">
													{t("your-mission.chat.update-message")}
												</IonText>
												<div className="grid grid-cols-2 gap-2">
													<Button
														data-attr="page.your-mission.chat.refine-proposal.decline"
														fill="clear"
														color="secondary"
														className="border-brown-200 rounded-lg border-2 text-sm font-bold"
														onClick={async () => {
															if (!message.missionProposal) return;
															setDeclinedProposals((prev) => new Set(prev).add(message.id));

															await declineProposalMutation.mutateAsync(message.missionProposal.id);
															chat.setStreamAssistantMessage(null);
															chat.setStreamUserMessage(t("your-mission.chat.no-message"), true);
															await mutationStream.mutateAsync({
																message: t("your-mission.chat.no-message"),
															});
														}}
													>
														{t("your-mission.chat.no")}
													</Button>
													<Button
														data-attr="page.your-mission.chat.refine-proposal.accept"
														onClick={async () => {
															if (!message.missionProposal) return;
															await acceptProposalMutation.mutateAsync(message.missionProposal.id);
															chat.setStreamAssistantMessage(null);
															chat.setStreamUserMessage(t("your-mission.chat.yes-message"), true);
															mutationStream.mutate({
																message: t("your-mission.chat.yes-message"),
															});
															router.push(Routes.YourMission(), "back");
														}}
													>
														{t("your-mission.chat.yes")}
													</Button>
												</div>
											</ChatMessage>
										)}
									</>
								)}
							</div>
						))}
					</ChatContent>
				</Content>
				<IonFooter>
					<ChatFooter>
						<ChatInput
							focusOnLoad
							onSubmit={async (content) => {
								chat.setStreamAssistantMessage(null);
								chat.setStreamUserMessage(content, true);
								mutationStream.mutate({ message: content });
								return true;
							}}
							canSendMessage={!unansweredProposal}
						/>
					</ChatFooter>
				</IonFooter>
			</ChatStoreContext.Provider>
		</IonPage>
	);
}
